import { IsActiveMatchOptions } from '@angular/router';
import { assign, cloneDeep, isObject, keys, merge, reduce } from 'lodash-es';
import { isMoment } from 'moment';
import mime from 'mime';
import { FormGroup } from '@angular/forms';
import { validateNRIC, validateUEN } from './validation.util';

export const SCREENWIDTH = {
  MOBILE: 768,
  TABLET: 992,
};

export class Helper {
  /**
   * Get the equivalent "IsActiveMatchOptions" options for "exact = true".
   */
  static get exactMatchOptions(): IsActiveMatchOptions {
    return {
      paths: 'exact',
      fragment: 'ignored',
      matrixParams: 'ignored',
      queryParams: 'exact',
    };
  }

  /**
   * Get the equivalent "IsActiveMatchOptions" options for "exact = false".
   */
  static get subsetMatchOptions(): IsActiveMatchOptions {
    return {
      paths: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
      queryParams: 'subset',
    };
  }

  static createFileFromBase64(base64: string, fileName: string): File {
    const byte = atob(base64.split(',')[1]);
    const mimetype = base64.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byte.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byte.length; i++) {
      ia[i] = byte.charCodeAt(i);
    }

    const extension = mime.getExtension(mimetype);
    const hasCorrectExtension = fileName.endsWith(`.${extension}`);
    const finalFileName = hasCorrectExtension ? fileName : `${fileName}.${extension}`;

    return new File([ab], finalFileName, { type: mimetype });
  }

  static createFileFromBlob(blob: Blob, fileName: string): File {
    return new File([blob], [fileName, mime.getExtension(blob.type)].filter(Boolean).join('.'), {
      lastModified: new Date().getTime(),
      type: blob.type,
    });
  }

  static openInNewTab(
    blob: Blob | MediaSource,
    target: '_self' | '_blank' | '_parent' | '_top' | '_unfencedTop'
  ): void {
    if (!(blob instanceof Blob) && !(blob instanceof MediaSource)) {
      throw new Error('Expected Blob or MediaSource');
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.target = target;
    link.click();

    // URL.revokeObjectURL(url); // Commented out because it may cause the file to not open in some browsers. The URL will be revoked when the page is unloaded.
  }

  static removePlusSign(value: string): string {
    return value.replace(/\+/g, '');
  }

  static getScreenWidth() {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  }

  static isMobileScreen = () => this.getScreenWidth() < SCREENWIDTH.MOBILE;
  static isTabletScreen = () =>
    this.getScreenWidth() < SCREENWIDTH.TABLET && this.getScreenWidth() >= SCREENWIDTH.MOBILE;
  static isDesktopScreen = () => this.getScreenWidth() > SCREENWIDTH.TABLET;

  static removeCommaFromText(txt: string) {
    let finalString = txt;
    if (txt && Object.prototype.toString.call(txt) === '[object String]') {
      finalString = finalString.replace(/,/g, '');
    }
    return +finalString;
  }

  static removeEmpty(data: any, exclude: string[] = []): any {
    if (!data) {
      return data;
    }
    switch (typeof data) {
      case 'object': {
        if (Array.isArray(data)) {
          return data.filter(
            item => !(item === null || item === undefined || item === '' || (Array.isArray(item) && !item.length))
          );
        }

        Object.keys(data).forEach(key => {
          if (!exclude.includes(key)) {
            const val = Helper.removeEmpty(data[key], exclude);
            if (val === null || val === undefined || val === '' || (Array.isArray(val) && !val.length)) {
              delete data[key];
            } else {
              data[key] = val;
            }
          }
        });
        return data;
      }
      case 'string': {
        return data.trim();
      }
      default:
        return data;
    }
  }

  static flatParams(params: Record<string, any>): object {
    if (!params) {
      return params;
    }
    Object.keys(params).forEach(key => {
      if (Array.isArray(params[key])) {
        params[key] = params[key].join(',');
      }
    });
    return params;
  }

  static slugify(input: string): string {
    return (
      input
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        // eslint-disable-next-line no-useless-escape
        .replace(/\-\-+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, '')
        .replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a')
        .replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e')
        .replace(/ì|í|ị|ỉ|ĩ/g, 'i')
        .replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o')
        .replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u')
        .replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y')
        .replace(/đ/g, 'd')
        // eslint-disable-next-line no-useless-escape
        .replace(/[^\w\-]+/g, '')
    ); // Remove all non-word chars;
  }

  static camelize(input: string): string {
    return (
      Helper.slugify(input)
        // eslint-disable-next-line no-useless-escape
        .replace(/\-(.)/g, char => char.toUpperCase()) // UpperCase first character
        .replace(/-/g, '')
    ); // Remove -
  }

  static groupBy(list: any[], keyGetter: (data: any) => string, converter?: (data: any) => any) {
    const map = new Map();
    list.forEach(item => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [converter ? converter(item) : item]);
      } else {
        collection.push(converter ? converter(item) : item);
      }
    });
    return map;
  }

  static isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  static mergeDeep(target: any, source: any): any {
    const output = Object.assign({}, target);
    if (Helper.isObject(target) && Helper.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (Helper.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = Helper.mergeDeep(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  }

  static leftJoin(target: any, source: any): any {
    const output = Object.assign({}, target);
    if (Helper.isObject(target) && Helper.isObject(source)) {
      Object.keys(target).forEach(key => {
        if (Helper.isObject(target[key])) {
          if (!(key in source)) {
            Object.assign(output, { [key]: target[key] });
          } else {
            output[key] = Helper.mergeDeep(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] || target[key] });
        }
      });
    }
    return output;
  }

  static isEqual(target: any, source: any): boolean {
    const targetType = typeof target;
    const sourceType = typeof source;
    if (targetType !== sourceType) {
      return false;
    }
    if (Array.isArray(target) && Array.isArray(source)) {
      const length = target.length;
      if (length !== source.length) {
        return false;
      }
      let check = true;
      for (let i = 0; i < length; i++) {
        check = Helper.isEqual(target[i], source[i]);
        if (!check) {
          break;
        }
      }
      return check;
    }

    if (Helper.isObject(target) && Helper.isObject(source)) {
      const targetValue = Object.values(target);
      const sourceValue = Object.values(source);
      return Helper.isEqual(targetValue, sourceValue);
    }

    return target === source;
  }

  static isEmpty(input: any): boolean {
    if (!input) {
      return true;
    }
    if (Array.isArray(input)) {
      return input.length === 0;
    }
    if (typeof input === 'object') {
      return Object.keys(input).length === 0;
    }
    return false;
  }

  static transformAddressObjectToString(objectAddress: {
    postalCode?: string;
    unitNumber?: string;
    blockNumber?: string;
    streetName?: string;
    buildingName?: string;
  }): string {
    const str =
      (objectAddress?.blockNumber ? objectAddress?.blockNumber : '') +
      ' ' +
      (objectAddress?.streetName ? objectAddress?.streetName : '') +
      ' ' +
      (objectAddress?.unitNumber ? objectAddress?.unitNumber : '') +
      ' ' +
      (objectAddress?.buildingName ? objectAddress?.buildingName : '') +
      ' SINGAPORE ' +
      (objectAddress?.postalCode ? objectAddress?.postalCode : '');
    return str.replace(/[^\S\r\n]{2,}/, ' ').trim();
  }

  static decodeJWT(token: string): Record<string, unknown> | null {
    const base64Url = token.split('.')[1];

    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    try {
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  /**
   * Encode a UTF-8 string into base64 safely in browser environments.
   * Falls back to an empty string on error.
   *
   * Usage: Helper.encodeToBase64(JSON.stringify(obj))
   */
  static encodeToBase64(input: string): string {
    if (typeof input !== 'string') return '';
    try {
      // Prefer TextEncoder to get UTF-8 bytes and then btoa the binary string.
      // This avoids using the deprecated `unescape` global.
      // eslint-disable-next-line no-undef
      if (typeof TextEncoder !== 'undefined') {
        const encoder = new TextEncoder();
        const bytes = encoder.encode(input);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
      }

      // Fallback for older environments: encodeURIComponent produces percent-encoded
      // UTF-8 bytes (e.g. %E2%9C%93). Convert each %xx to the corresponding byte
      // and then use btoa on the resulting binary string.
      return btoa(
        encodeURIComponent(input).replace(/%([0-9A-F]{2})/g, (_match, p1) => String.fromCharCode(parseInt(p1, 16)))
      );
    } catch (err) {
      // If an error occurred above, attempt TextEncoder-based approach as a last resort
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const encoder: any = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
        if (encoder) {
          const bytes = encoder.encode(input);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
          return btoa(binary);
        }
      } catch (e) {
        // Fall through to throw below
      }
      // Do not silently swallow encoding errors - surface to caller so they can handle it.
      // Provide a helpful message while preserving original error when available.
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(`Helper.encodeToBase64 failed: ${message}`);
    }
  }

  /**
   * Decode a base64-encoded UTF-8 string back to a normal string.
   * Returns empty string on error.
   */
  static decodeFromBase64(base64: string): string {
    if (typeof base64 !== 'string' || !base64) return '';
    try {
      // atob gives a binary string (each char code 0-255). Convert to percent-encoded UTF-8 and decode.
      const binary = atob(base64);
      const bytes = Array.prototype.map
        .call(binary, (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('');
      return decodeURIComponent(bytes);
    } catch (err) {
      try {
        return atob(base64);
      } catch (e) {
        return '';
      }
    }
  }

  /**
   * Generates a random id
   *
   * @param length
   */
  static randomId(length: number = 10): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let name = '';

    for (let i = 0; i < length; i++) {
      name += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return name;
  }

  static clearMoment(data: any, config?: { milliseconds: boolean }): any {
    if (isMoment(data)) {
      if (config?.milliseconds) {
        return data.valueOf();
      }
      return data.toDate().toISOString();
    }
    if (Array.isArray(data)) {
      return data.map(item => Helper.clearMoment(item, config));
    }
    if (data instanceof Date) {
      return data.toISOString();
    }
    if (isObject(data)) {
      const res = reduce(
        keys(cloneDeep(data)).map(key => {
          return { [key]: Helper.clearMoment((data as Record<string, unknown>)[key], config) };
        }),
        (acc, item) => {
          return assign(acc, item);
        },
        {}
      );
      return res;
    }

    return data;
  }

  static momentToDate(data: any): any {
    if (isMoment(data)) {
      return data.toDate();
    }
    if (Array.isArray(data)) {
      return data.map(item => Helper.momentToDate(item));
    }
    if (data instanceof Date) {
      return data;
    }
    if (isObject(data)) {
      const res = reduce(
        keys(cloneDeep(data)).map(key => {
          return { [key]: Helper.momentToDate((data as Record<string, unknown>)[key]) };
        }),
        (acc, item) => {
          return assign(acc, item);
        },
        {}
      );
      return res;
    }

    return data;
  }

  static round(num: number, precision: number) {
    return precision > -1
      ? Math.round((num + Number.EPSILON) * Math.pow(10, precision)) / Math.pow(10, precision)
      : num;
  }

  static floor(num: number, precision: number) {
    return precision > -1
      ? Math.floor((num + Number.EPSILON) * Math.pow(10, precision)) / Math.pow(10, precision)
      : num;
  }

  static isNricNumber(nric: string): boolean {
    return validateNRIC(nric);
  }

  static isUENNumber(uen: string): boolean {
    return validateUEN(uen);
  }

  static matchRule(rule: string, candidate: string) {
    return new RegExp('^' + rule.split('*').join('.*') + '$').test(candidate);
  }

  static toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      value = value.toString();
    }

    if (typeof value === 'string') {
      switch (value?.toLowerCase()?.trim()) {
        case 'true':
        case 'yes':
        case 'y':
        case '1':
          return true;
        case 'false':
        case 'no':
        case 'n':
        case '0':
        case 'null':
        case 'undefined':
          return false;
        default:
          return false;
      }
    }

    // TODO: handle value is function or object

    return false;
  }

  static logFormError(name: string, form: FormGroup): void {
    console.group(name, 'Form Error');
    for (const key in form.controls) {
      if (Object.prototype.hasOwnProperty.call(form.controls, key)) {
        const element = form.controls[key];
        if (element.invalid) console.log(key, element.value, element.errors);
      }
    }
    console.groupEnd();
  }

  static getFileNameWithoutExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // If there is no dot in the filename, return the filename itself
      return filename;
    } else {
      // Otherwise, return the filename up to the last dot
      return filename.substring(0, lastDotIndex);
    }
  }

  // Add a plus sign at the beginning
  static addPlusSignAtTheBeginning(areaCode: string): string {
    return `+${areaCode.replace(/\+/g, '')}`;
  }

  static addComma(str: number): string {
    const rgx = /(\d+)(\d{3})/;
    const x: string[] = str.toFixed(2).split('.');

    let x1: string = x[0];
    const x2: string = x.length > 1 ? '.' + x[1] : '';

    while (rgx.test(x1)) {
      x1 = x1.replace(rgx, '$1' + ',' + '$2');
    }

    return x1 + x2;
  }

  static addCurrency(nbr: number): string {
    return nbr > 0 ? '$' : '';
  }

  static sortByOrder(a: any, b: any) {
    return a.metadata?.order - b.metadata?.order;
  }

  static onePercent(value: number): number {
    return value * 0.01;
  }

  static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toFixed(1)} MB`;
    return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  }

  static mergeDeepWithoutMergeArray(objValue: any, srcValue: any) {
    // Ignore falsy srcValue
    if (!srcValue) {
      return objValue;
    }

    if (Array.isArray(objValue) && Array.isArray(srcValue)) {
      return srcValue;
    }

    // If both values are objects, merge them
    if (isObject(objValue) && isObject(srcValue)) {
      return merge({}, objValue, srcValue);
    }

    // Otherwise, return srcValue (overwriting the existing value)
    return srcValue;
  }
}
